@import 'styles/mixins';
@import 'styles/blaze/mixins';
@import 'styles/colors';
@import 'styles/blaze/colors';

.root {
  margin-bottom: 0;

  --menu-item-color: var(--dark-80);
  --menu-item-background-color-hover: var(--light-80);
  --menu-item-color-active: var(--light-100);
  --menu-item-background-color-active: var(--selected-fill);
  --menu-item-background-color-active-hover: var(--selected-fill);
  --menu-item-background-color-focus: var(--light-80);
  --menu-item-icon-color: var(--dark-80);
  --menu-item-checkbox-color: var(--dark-40);
  --menu-item-chevron-color: var(--dark-40);
  --menu-section-header-color: var(--dark-60);

  [data-autopilot-system-redesign='on'] & {
    --menu-item-color-active: var(--dark-90);
  }

  display: flex;
  flex-direction: column;
  gap: 2px;

  &.tray {
    gap: 0;
  }
}

.focusRing {
  outline: none;
  background: var(--override-menu-item-background-color-focus, var(--menu-item-background-color-focus));
}

.subMenuItem {
  outline: none;
}

.item {
  @include sm-sohne-medium;
  display: flex;
  align-items: center;
  color: var(--override-menu-item-color, var(--menu-item-color));
  padding: 0 8px;
  outline: none;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  min-height: 40px;

  [data-autopilot-system-redesign='on'] & {
    @include sm-sohne;
    color: var(--dark-90);
  }

  .tray & {
    padding: 0;
    border-radius: 0;
    min-height: 48px;

    .frontIcon {
      align-self: flex-start;
      min-height: 48px;
    }
  }

  .frontIcon {
    display: flex;
    align-items: center;
  }
}

.itemContent {
  display: flex;
  flex: 1;
  align-items: center;
  column-gap: 8px;
  @include line-clamp;

  > svg {
    width: 20px;
    height: 20px;

    [data-autopilot-system-redesign='on'] & {
      width: 16px;
      height: 16px;
    }
  }

  svg {
    color: var(--override-menu-item-icon-color, var(--menu-item-icon-color));
    flex-shrink: 0;

    [data-autopilot-system-redesign='on'] & {
      color: var(--dark-90);
    }
  }

  .itemLabelContainer {
    display: flex;
    flex-direction: column;
    flex: 1 0 0;
    gap: 2px;
    min-width: 0;

    .itemLabelContent {
      display: flex;
      flex: 1 0 0;
      align-items: center;
      gap: 1px;
      min-width: 0;
    }

    .tray & {
      min-height: 48px;
      padding: 10px 0;
      border-bottom: 1px solid var(--dark-4);
    }

    .itemLabel {
      display: flex;
      align-items: center;
      flex: 1 0 0;
      justify-content: space-between;
      min-width: 0;
    }

    .description {
      display: flex;
      min-width: 0;
      white-space: normal;
    }
  }
}

.itemHovered {
  background: var(--dark-4);

  [data-autopilot-system-redesign='on'] & {
    background: var(--dark-3);
  }

  @include for-phone-only {
    background: transparent;
    box-shadow: none;
  }
}

.itemSelected {
  position: relative;

  &.checkbox {
    .itemCheckbox {
      border: 0;
    }
  }

  &:not(.checkbox) {
    background: var(--override-menu-item-background-color-active, var(--menu-item-background-color-active));
    color: var(--override-menu-item-color-active, var(--menu-item-color-active));

    .itemCheckIcon,
    svg {
      color: var(--override-menu-item-color-active, var(--menu-item-color-active));
    }

    .tray & {
      background: inherit;
      color: inherit;

      .itemCheckIcon,
      svg {
        color: inherit;
      }

      .check {
        border-radius: 50%;
        height: 24px;
        width: 24px;
        display: flex;
        margin-top: -4px;
        margin-bottom: -4px;
        align-items: center;
        justify-content: center;
        background: var(--selected-fill);
        color: var(--override-menu-item-color-active, var(--menu-item-color-active));

        [data-autopilot-system-redesign='on'] & {
          height: 16px;
          width: 16px;
          background: var(--dark-80);
          color: var(--light-100);
        }
      }
    }

    &:hover {
      background: var(
        --override-menu-item-background-color-active-hover,
        var(--menu-item-background-color-active-hover)
      );

      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #000000;
        opacity: 0.05;
        transition: opacity 0.2s ease;
        border-radius: inherit;
        pointer-events: none;
      }
    }

    [data-autopilot-system-redesign='on'] & {
      border-radius: 4px;
      border: none;
      background: var(--dark-4);
      color: var(--dark-90);
      font-weight: 500;

      &:hover {
        background: var(--dark-3);
      }
    }
  }
}

.checkIcon,
.check {
  [data-autopilot-system-redesign='on'] & {
    background: var(--dark-80);
    color: var(--light-100);
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      color: var(--light-100);
    }
  }
}

.itemDisabled {
  opacity: 0.4;
  color: var(--light-100);
  cursor: not-allowed;
}

.itemCheckIcon {
  color: var(--override-menu-item-icon-color, var(--menu-item-icon-color));
  margin-left: 16px;
}

.itemCheckbox {
  display: flex;
  margin-right: 6px;
  color: var(--override-menu-item-checkbox-color, var(--menu-item-checkbox-color));
  width: 20px;
  height: 20px;

  svg {
    fill: transparent;
  }
}

.checkbox {
  .itemCheckbox {
    border-radius: 2px;
    border: 1px solid var(--dark-40);
    background: var(--light-100);
  }
}

.chevron {
  color: var(--override-menu-item-chevron-color, var(--menu-item-chevron-color));
  display: flex;
}

@media (min-width: $tabletPortraitMinWidth) {
  .item {
    min-height: 36px;
  }
}
